import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card } from "reactstrap";

import { ReactComponent as BackIcon } from "assets/img/svg/BackArrow.svg";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import ALoader from "views/pages/manage/common/a-loader";
import { BUPopover } from "views/pages/manage/unAssigned/studentFinanceApproval/epmrCancellation/constant";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";

import {
    fetchStateAndYearDropdowns,
    filterBuByButypeAndSubType,
    searchCommissionSlab,
} from "./helper";
import { gridStyles } from "views/pages/manage/edpOps/issuanceIDcard/printIdCardAndMapRfid/constant";
import EnhanceDataGrid from "views/pages/manage/common/data-grid/EnhanceDataGrid";
import { filterOwnedBu } from "../../accounting/commissionReport/utils";

const DEFAULT_PAGE_SIZE = 50;

const CommissionSlabs = ({ userPermissions }) => {
    const history = useHistory();


    const masterDropdowns = useSelector(
        (state) => state?.globalData?.masterDropdowns
    );


    const [dropdownOptions, setDropdownOptions] = useState({
        yearOptions: [],
    });

    const [form, setForm] = useState({
        bu: [],
        year: [],
    });

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState(null);
    const [buDescriptionMap, setBuDescriptionMap] = useState({});

    const buOptions = useMemo(() => {
        const bu = (masterDropdowns?.businessArea || []).map((item) => ({
            label: item.label,
            value: item.businessAreaId,
            ...item
        }));
        return filterBuByButypeAndSubType(
            bu,
            dropdownOptions?.businessUnitType,
            'SubContractor',
            'F+EC'
        );

    }, [masterDropdowns, dropdownOptions]);

    const isFormValid = useMemo(() => {
        return form?.bu?.length > 0 || form?.year?.length > 0;
    }, [form]);


    useEffect(() => {
        loadDropdowns();
    }, []);

    const loadDropdowns = async () => {
        try {
            const response = await fetchStateAndYearDropdowns();
            setDropdownOptions(response || {});
        } catch (err) {
            console.error("Dropdown fetch failed:", err);
        }
    };


    const handleFormChange = useCallback((key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const buildPayload = () => ({
        businessUnits: form?.bu?.map((item) => item.label) || [],
        years: form?.year?.map((item) => item.value) || [],
    });

    const handleSearch = async (page = currentPage, size = pageSize) => {
        setLoading(true);
        setError(null);

        try {
            const payload = buildPayload();

            const response = await searchCommissionSlab(payload, page, size);

            const content = response?.data?.content || [];
            const totalElements = response?.data?.totalElements || 0;

            setRows(content);
            setTotalCount(totalElements);
            setPageSize(size);
            setCurrentPage(page);
        } catch (err) {
            console.error("Search failed:", err);
            setRows([]);
            setTotalCount(0);
            setError("Failed to fetch commission slabs. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (params) => {
        handleSearch(params.page, pageSize);
    };

    const handlePageSizeChange = (params) => {
        handleSearch(params?.page, params.pageSize);
    };


    const columns = useMemo(
        () => [
            new AGridColDef("businessUnit", "Business Area")
                .setWidth(150)
                .renderCellComponent((params) => params.value ? <div className="d-flex align-items-center gap-2">
                    {params.value || "-"}
                    <BUPopover
                        businessArea={params?.row?.businessUnit}
                        businessAreaOptions={buOptions || []}
                        buDescriptionMap={buDescriptionMap}
                        setBuDescriptionMap={setBuDescriptionMap}
                    />
                </div> : "-"),


            new AGridColDef("extensionBusinessUnit", "Extension BU")
                .setWidth(150)
                .renderCellComponent((params) => params.value ? <div className="d-flex align-items-center gap-2">
                    {params.value || "-"}
                    <BUPopover
                        businessArea={params?.row?.extensionBusinessUnit}
                        businessAreaOptions={buOptions || []}
                        buDescriptionMap={buDescriptionMap}
                        setBuDescriptionMap={setBuDescriptionMap}
                    />
                </div> : "-"),


            new AGridColDef("year", "Year")
                .setWidth(100)
                .renderCellComponent((params) => params.value || "-"),

            new AGridColDef("action", "Actions", false)
                .setMinWidth(120)
                .pinColumnLast(true)
                .renderCellComponent((params) => (
                    <AButton
                        variant="primary_filled"
                        // size="xs"
                        onClick={() =>
                            history.push(`/admin/commissionSlab/${params.row.id}`)
                        }
                    >
                        View
                    </AButton>
                )),
        ],
        [history]
    );


    return (<> 
    {loading && (

        <ALoader position="fixed" />

    )}
        <div className="d-flex flex-column p-4 w-100">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <IconButtonWrapper onClick={() => history.goBack()}>
                        <BackIcon />
                    </IconButtonWrapper>
                    <h2 className="color-black-60 text-bold mb-0 ml-2">
                        Sub-Contractor F+EC Commission Slab
                    </h2>
                </div>

                <AButton
                    variant="primary_filled"
                    onClick={() => history.push("/admin/commissionSlab/new")}
                    disabled={loading || !(userPermissions || []).includes("C")}
                >
                    Add New Slab
                </AButton>
            </div>

            {/* Search Card */}
            <Card className="p-4 mb-4">
                <h3 className="mb-3 text-bold">Search Commission Slab</h3>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr) auto",
                        gap: "16px",
                        maxWidth: "800px",
                    }}
                >
                    <AAutoComplete
                        label="Business Unit"
                        placeholder="Select"
                        isMulti
                        isShowCount
                        items={buOptions}
                        currentValue={form.bu}
                        handleChange={(value) => handleFormChange("bu", value)}
                        height="45px"
                    />

                    <AAutoComplete
                        label="Year"
                        placeholder="Select"
                        isMulti
                        isShowCount
                        items={dropdownOptions?.yearOptions || []}
                        currentValue={form.year}
                        handleChange={(value) => handleFormChange("year", value)}
                        height="45px"
                    />

                    <AButton
                        variant="primary_filled"
                        disabled={!isFormValid || loading}
                        onClick={() => handleSearch(0, pageSize)}
                        className="mt-auto"
                    >
                        {loading ? "Searching..." : "Search"}
                    </AButton>
                </div>
            </Card>

            {/* Loader */}


            {/* Error */}
            {error && (
                <Card className="p-3 text-danger mb-3">
                    {error}
                </Card>
            )}

            {/* Table */}
            <Card className="px-4" style={{ height: "100%" }}>
                <EnhanceDataGrid
                    removeWrapperContainer
                    paginationModel={{ page: currentPage, pageSize: pageSize }}
                    rows={rows || []}
                    columns={columns}
                    rowId={(row) => row.id}
                    rowHeight={50}
                    loading={false}
                    rowCount={totalCount}
                    nextButtonHandler={(pageNo, pageSize) => handlePageSizeChange({ page: pageNo, pageSize })}
                    currentPage={currentPage}
                    sx={gridStyles}
                    autoHeight
                    pinLastColumn
                    emptyMessage="No commission slabs found."
                />
            </Card>
        </div>
    </>
    );
};

export default CommissionSlabs;
